import type { ReactNode } from "react";
import {
  Bot,
  ChartNoAxesCombined,
  CircleCheckBig,
  ClipboardCheck,
  FileCheck,
  Gauge,
  GitBranchPlus,
  Landmark,
  LifeBuoy,
  Radio,
  Route,
  ShieldPlus,
  Sparkles,
  Target,
  Wrench,
  BriefcaseBusiness,
  Activity,
  Layers,
  AlertTriangle,
} from "lucide-react";
import type { TFunction } from "i18next";
import { featureFlags } from "../../config/featureFlags";
import type { ComplianceCenterNavSection } from "../../components/compliance-center/ComplianceCenterNavTabs";

/** IA refactor (Phase Compliance Center): visible grouped navigation only. Routes for hidden modules remain registered. */
export function buildComplianceCenterNavSections(t: TFunction): ComplianceCenterNavSection[] {
  const complianceOperations = [
    {
      key: "overview",
      label: t("complianceCenterUi.nav.overview", "Overview"),
      to: "/compliance-center/overview",
      icon: <Gauge size={16} aria-hidden />,
    },
    {
      key: "assessments",
      label: t("complianceCenterUi.nav.assessments", "Standards & Assessments"),
      to: "/compliance-center/assessments",
      icon: <Target size={16} aria-hidden />,
    },
    ...(featureFlags.CONTINUOUS_COMPLIANCE_OPERATIONS_V1
      ? [
          {
            key: "operations",
            label: t("complianceCenterUi.nav.operations", "Compliance Operations"),
            to: "/compliance-center/operations",
            icon: <Activity size={16} aria-hidden />,
          },
        ]
      : []),
    {
      key: "evidence",
      label: t("complianceCenterUi.nav.evidence", "Evidence Hub"),
      to: "/compliance-center/evidence",
      icon: <FileCheck size={16} aria-hidden />,
    },
    {
      key: "remediation",
      label: t("complianceCenterUi.nav.remediation", "Recommendations & Remediation"),
      to: "/compliance-center/remediation",
      icon: <Wrench size={16} aria-hidden />,
    },
    {
      key: "risk-register",
      label: t("complianceCenterUi.nav.riskRegister", "Risk Register"),
      to: "/compliance-center/risk-register",
      icon: <AlertTriangle size={16} aria-hidden />,
    },
    {
      key: "audits",
      label: t("complianceCenterUi.nav.internalAuditCenter", "Internal Audit Center"),
      to: "/compliance-center/audits",
      icon: <ClipboardCheck size={16} aria-hidden />,
    },
    {
      key: "certification",
      label: t("complianceCenterUi.nav.certification", "Certification"),
      to: "/compliance-center/certification",
      icon: <CircleCheckBig size={16} aria-hidden />,
    },
    {
      key: "trust-center",
      label: t("complianceCenterUi.nav.trustCenter", "Trust Center"),
      to: "/compliance-center/trust-center",
      icon: <LifeBuoy size={16} aria-hidden />,
    },
  ];

  const continuousMonitoring = [
    {
      key: "automation",
      label: t("complianceCenterUi.nav.continuousMonitoring", "Continuous Monitoring & Drift"),
      to: "/compliance-center/automation",
      icon: <Sparkles size={16} aria-hidden />,
    },
  ];

  const governanceIntelligence: ComplianceCenterNavSection["items"] = [
    {
      key: "ai-governance-conversation",
      label: t("complianceCenterUi.nav.aiCiso", "AI CISO"),
      to: "/compliance-center/ai-governance-conversation",
      icon: <Bot size={16} aria-hidden />,
    },
  ];
  // Proactive materialization workspace remains separately gated (unrelated unfinished surface).
  if (featureFlags.AI_GOVERNANCE_INTELLIGENCE_SURFACE_V1) {
    governanceIntelligence.unshift({
      key: "ai-governance-intelligence",
      label: t("complianceCenterUi.nav.aiGovernanceIntelligence", "AI Governance Intelligence"),
      to: "/compliance-center/ai-governance-intelligence",
      icon: <Sparkles size={16} aria-hidden />,
    });
  }

  const governanceManagement = [
    {
      key: "policies",
      label: t("complianceCenterUi.nav.policies", "Policies & Governance"),
      to: "/compliance-center/policies",
      icon: <ShieldPlus size={16} aria-hidden />,
    },
  ];

  const sections: ComplianceCenterNavSection[] = [
    {
      key: "compliance-operations",
      label: t("complianceCenterUi.nav.groupComplianceOperations", "Compliance Operations"),
      items: complianceOperations,
    },
    {
      key: "continuous-monitoring",
      label: t("complianceCenterUi.nav.groupContinuousMonitoring", "Continuous Monitoring"),
      items: continuousMonitoring,
    },
  ];

  if (governanceIntelligence.length > 0) {
    sections.push({
      key: "governance-intelligence",
      label: t("complianceCenterUi.nav.groupGovernanceIntelligence", "Governance Intelligence"),
      items: governanceIntelligence,
    });
  }

  sections.push({
    key: "governance-management",
    label: t("complianceCenterUi.nav.groupGovernanceManagement", "Governance Management"),
    items: governanceManagement,
  });

  return sections;
}

/**
 * Hidden from visible nav (routes preserved):
 * - operational-workflow, operational-events, executive-governance, control-lifecycle,
 *   ai-assistant, executive, grc-admin, cross-standard-reuse, operational-assurance
 */
export const HIDDEN_COMPLIANCE_CENTER_NAV_KEYS = [
  "operational-workflow",
  "operational-events",
  "executive-governance",
  "control-lifecycle",
  "ai-assistant",
  "executive",
  "grc-admin",
  "cross-standard-reuse",
  "operational-assurance",
] as const;

/** Legacy flat list for tests or deep-link tooling — not used for primary nav rendering. */
export function buildLegacyComplianceCenterNavItems(t: TFunction): Array<{
  key: string;
  label: string;
  to: string;
  icon?: ReactNode;
}> {
  return [
    ...buildComplianceCenterNavSections(t).flatMap((section) => section.items),
    { key: "ai-assistant", label: t("complianceCenterUi.nav.aiAssistant", "AI Assistant"), to: "/compliance-center/ai-assistant", icon: <Bot size={16} aria-hidden /> },
    { key: "control-lifecycle", label: t("complianceCenterUi.nav.controlLifecycle", "Control Lifecycle"), to: "/compliance-center/control-lifecycle", icon: <GitBranchPlus size={16} aria-hidden /> },
    { key: "executive", label: t("complianceCenterUi.nav.executive", "Executive & Board"), to: "/compliance-center/executive", icon: <ChartNoAxesCombined size={16} aria-hidden /> },
    { key: "grc-admin", label: t("complianceCenterUi.nav.grcAdmin", "GRC Admin"), to: "/compliance-center/grc-admin", icon: <BriefcaseBusiness size={16} aria-hidden /> },
    ...(featureFlags.COMPLIANCE_WORKFLOW_V1
      ? [{ key: "operational-workflow", label: t("complianceCenterUi.nav.operationalWorkflow", "Operational Workflow"), to: "/compliance-workflow", icon: <Route size={16} aria-hidden /> }]
      : []),
    ...(featureFlags.COMPLIANCE_REUSE_V1
      ? [{ key: "cross-standard-reuse", label: t("complianceCenterUi.nav.crossStandardReuse", "Cross-standard reuse"), to: "/compliance-center/cross-standard-reuse", icon: <Layers size={16} aria-hidden /> }]
      : []),
    ...(featureFlags.CONTROL_ASSURANCE_V1
      ? [{ key: "operational-assurance", label: t("complianceCenterUi.nav.operationalAssurance", "Operational assurance"), to: "/compliance-center/operational-assurance", icon: <Activity size={16} aria-hidden /> }]
      : []),
    ...(featureFlags.COMPLIANCE_RUNTIME_V1
      ? [{ key: "operational-events", label: t("complianceCenterUi.nav.operationalEvents", "Operational events"), to: "/compliance-center/operational-events", icon: <Radio size={16} aria-hidden /> }]
      : []),
    ...(featureFlags.EXECUTIVE_GOVERNANCE_V1
      ? [{ key: "executive-governance", label: t("complianceCenterUi.nav.executiveGovernance", "Executive Governance"), to: "/compliance-center/executive-governance", icon: <Landmark size={16} aria-hidden /> }]
      : []),
  ];
}
